import { action, computed, makeObservable, observable } from 'mobx';
import pick from 'lodash/pick';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { Error, pageError } from '../../core/error';
import { JobModel } from '../../entities';
import { MomentSelector } from '../../entities/moments';
import { Routes } from '../../routes';
import { MomentCloudMode, MomentCloudState } from '../../components/momentCloud';
import { Maybe } from '../../core';
import { PlayerItemSource } from '../../entities/player/playerItemSource';
import { MomentSplashState } from '../../components/momentSplash';
import { getApiResultJobError } from '../../api';

export type UserSplashPageParams = {
  jobId?: string
}

type Props = BindingProps<{
  selector?: MomentSelector,
  suppressApiQueries?: boolean
}>

export type SplashPageSections =
  'Topics' |
  'SubTopics' |
  'Speakers';

export class UserSplashPageState
  extends StoreNode {

  readonly nodeType: 'UserSplashPage' = 'UserSplashPage';

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'setError' | 'setLoaded' | 'setSkipPage'>(this, {
      selector: computed,
      suppressApiQueries: computed,
      isLoading: observable,
      error: observable,
      skipPage: observable,
      jobId: observable,
      job: computed,
      mode: computed,
      setMode: action,
      isExperimentalMode: observable,
      toggleExperimentalMode: action,
      mounted: action,
      unmounted: action,
      reset: action,
      setError: action,
      setLoaded: action,
      setSkipPage: action,
      shouldSkipPage: action,
    });

    this.defaultSelector = new MomentSelector(store, {
      jobId: () => this.jobId
    });

    this.momentCloud = new MomentCloudState(store, {
      jobId: () => this.jobId,
      selector: () => this.selector
    });
    this.momentSplash = new MomentSplashState(store, {
      jobId: () => this.jobId,
      selector: () => this.selector
    });

    this.playerItemSource = new PlayerItemSource(store, {
      jobId: () => this.jobId,
      selector: () => this.selector
    });
  }

  readonly defaultSelector: MomentSelector;

  get selector(): MomentSelector {
    const propSelector = this.getResolvedProp('selector');
    if (!propSelector)
      return this.defaultSelector;

    return propSelector;
  }

  get suppressApiQueries(): boolean {
    return this.getResolvedProp('suppressApiQueries', false);
  }

  isLoading: boolean = true;
  error: Error | null = null;
  skipPage: boolean = false;

  jobId: string | null = null;
  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  readonly momentCloud: MomentCloudState;
  readonly momentSplash: MomentSplashState;
  readonly playerItemSource: PlayerItemSource;

  get mode() {
    return this.momentCloud.mode;
  }
  setMode(mode: MomentCloudMode) {
    this.selector.setMode(mode);
  }

  isExperimentalMode = false;

  toggleExperimentalMode() {
    this.isExperimentalMode = !this.isExperimentalMode;
  }

  /** Notifies the state that the landing page has been mounted and triggers the initialization tasks. */
  async mounted(params?: Maybe<UserSplashPageParams>) {

    const { jobId } = params || {};
    if (!jobId)
      return this.setError();

    const { store } = this;
    this.jobId = jobId;
    this.isLoading = true;

    if (!this.suppressApiQueries) {
      const [res, apiErr] = await store.apiFetchJob(jobId, true);

      const err = getApiResultJobError([[res, apiErr]]);
      if (err)
        return this.setError(err);
    }

    // check if this is the response that came after the last request,  otherwise ignore it
    if (jobId !== this.jobId) {
      console.warn(`Response for Job '${jobId}' has been discarded.`);
      return false;
    }

    if (!this.job)
      return this.setError();

    if (!this.job.isPublished)
      return this.setError(new Error('JobUnavailable'));

    this.broadcast('videoSplashLoaded', {
      jobId,
      title: this.job?.title,
      path: Routes.userSplash(jobId)
    });

    this.selector.load();
    this.selector.exitVideoMode();

    if (this.shouldSkipPage()) {
      this.setSkipPage();
      return;
    }

    // not needed as page should be skipped
    // if (this.selector.mode === 'Topics' && !this.selector.hasTopics) 
    //   this.selector.setMode('Speakers');

    if (this.selector.mode === 'Speakers' && !this.selector.hasSpeakers)
      this.selector.setMode('Topics');

    this.setLoaded();
  }

  /** Notifies the state that the landing page has been unmounted and triggers the cleanup tasks. */
  unmounted() {
    this.selector.save();
    this.reset();

    // TEMP: fix to prevent the graph briefly being displayed before the load mask appears
    this.isLoading = true;
  }

  // #region State helpers
  reset() {
    this.selector.reset();
    this.skipPage = false;
    this.jobId = null;
    this.isLoading = false;
    this.error = null;
  }

  private setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  private setLoaded() {
    this.isLoading = false;
    this.error = null;
  }
  // #endregion
  private setSkipPage(): void {
    if (!this.jobId)
      return;

    this.skipPage = true;
  }

  shouldSkipPage(): boolean {
    if (!this.selector.hasTopics) //&& !this.selector.hasSpeakers
      return true;

    // const search = new URLSearchParams(window.location.search);
    // const teamId = decodeURI(search.get('teamId') || '');

    // if (teamId)
    //   return true;

    return false;
  }

  private _exportJobData() {

    const data: Record<string, unknown> = pick(this.job, [
      'id',
      'title',
      'status'
    ]);

    data.moments = this.job?.moments.map(mom => {

      return pick(mom, [
        'id',
        'momentType',
        'startTime',
        'endTime',
        'description',
        'jobId',
        'source',
        'speakerId',
        'trackId'
      ]);
    });

    data.speakers = this.job?.speakers.map(spk => {

      return pick(spk, [
        'id',
        'jobId',
        'name',
        'description',
        'picture',
        'scope'
      ]);
    });

    return data;
  }

  getSectionVisibility = (section: SplashPageSections) => {
    switch (section) {
      case 'Speakers':
        return this.selector.hasSpeakers;
      case 'Topics':
        return this.selector.hasTopics;
      case 'SubTopics':
        return this.selector.hasSubTopics;
      default:
        return true;
    }
  }
}